import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import SearchRounded from '@mui/icons-material/SearchRounded'
import { Autocomplete, InputAdornment, TextField } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { GameRef, GenreTaxonomy } from '../../types'

type SearchOption = { type: 'game'; game: GameRef } | { type: 'genre'; genre: GenreTaxonomy }

interface UAInsightsSearchBarProps {
  games: GameRef[]
  genres: GenreTaxonomy[]
  onSelectGame: (gameId: string) => void
  onSelectGenre: (genreId: string) => void
}

export const UAInsightsSearchBar: FC<UAInsightsSearchBarProps> = ({ games, genres, onSelectGame, onSelectGenre }) => {
  const { t } = useTranslation('ua-insights')
  const { genreLabel } = useUATaxonomy()

  const optionLabel = (option: SearchOption) => (option.type === 'game' ? option.game.name : genreLabel(option.genre))

  const options: SearchOption[] = [
    ...[...genres].sort((a, b) => genreLabel(a).localeCompare(genreLabel(b))).map((genre): SearchOption => ({ type: 'genre', genre })),
    ...[...games].sort((a, b) => a.name.localeCompare(b.name)).map((game): SearchOption => ({ type: 'game', game })),
  ]

  const [value, setValue] = useState<SearchOption | null>(null)

  return (
    <Autocomplete
      value={value}
      options={options}
      getOptionLabel={optionLabel}
      groupBy={(option) => (option.type === 'game' ? t('search_group_games') : t('search_group_genres'))}
      renderOption={(props, option) => {
        // The default option key is the label, but a genre and a game can share
        // a name (e.g. "Number Match"), so key on the stable id instead to
        // avoid duplicate-key collisions. Strip the incoming key so ours wins.
        const { key: _key, ...optionProps } = props as typeof props & { key?: React.Key }
        const optionKey = option.type === 'game' ? `game-${option.game.id}` : `genre-${option.genre.id}`
        return (
          <li {...optionProps} key={optionKey}>
            {optionLabel(option)}
          </li>
        )
      }}
      blurOnSelect
      onChange={(_event, selected) => {
        if (!selected) {
          setValue(null)
          return
        }
        if (selected.type === 'game') onSelectGame(selected.game.id)
        else onSelectGenre(selected.genre.id)
        setValue(null)
      }}
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('search_placeholder')}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.5, mr: 1 }}>
                <SearchRounded sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: borderRadiusDefault,
              backgroundColor: 'background.paper',
              paddingLeft: 1.5,
              transition: 'box-shadow 0.2s ease',
              '&:hover': { boxShadow: 2 },
              '&.Mui-focused': { boxShadow: 3 },
            },
            '& .MuiAutocomplete-input': {
              padding: '2.5px 4px 2.5px 0 !important',
            },
          }}
        />
      )}
    />
  )
}
